import React from 'react';
import { Box, Typography, Stack, Button, Paper, Alert } from '@mui/material';
import { Link as LinkRouter } from 'react-router-dom';
import AuthLayout from '../../../../../components/auth/AuthLayout';
import { getPolyglot } from '../../../../../i18n';
import {
  Invitation,
  InvitationState,
} from '../../../../../redux/invitations/invitation.model';
import SC_background from '../../../assets/scc_login_image.jpg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import { isInviationValid } from './invitationValidations';

interface InvitationRequestPageProps<T> {
  invitation: Invitation | undefined;
  invitationId: string | null;
  onAccept: () => void;
  onReject: () => void;
  onBackToLogin: () => void;
  isLoading?: boolean;
}

const InvitationRequestPage = <T,>({
  invitation,
  invitationId,
  onAccept,
  onReject,
  onBackToLogin,
  isLoading = false,
}: InvitationRequestPageProps<T>) => {
  const polyglot = getPolyglot();

  // Handle APPROVED status
  if (invitation?.status === InvitationState.APPROVED) {
    return (
      <AuthLayout imageRight={SC_background}>
        <Box
          sx={{
            margin: 2,
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 80, color: 'success.main', marginBottom: 2 }}
          />
          <Typography variant="h1" gutterBottom>
            {polyglot.t('freshwatch.invitation.already_accepted_title') ||
              'Already Accepted'}
          </Typography>
          <Alert
            severity="info"
            sx={{
              marginTop: 3,
              marginBottom: 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">
              {polyglot.t('freshwatch.invitation.already_accepted_message') ||
                'This invitation has already been accepted.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={onBackToLogin}
            component={LinkRouter}
            to="/login"
          >
            {polyglot.t('freshwatch.invitation.back_to_login') ||
              'Go back to Login'}
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  // Handle REJECTED status
  if (invitation?.status === InvitationState.REJECTED) {
    return (
      <AuthLayout imageRight={SC_background}>
        <Box
          sx={{
            margin: 2,
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          <CancelIcon
            sx={{ fontSize: 80, color: 'error.main', marginBottom: 2 }}
          />
          <Typography variant="h1" gutterBottom>
            {polyglot.t('freshwatch.invitation.rejected_title') ||
              'Invitation Rejected'}
          </Typography>
          <Alert
            severity="warning"
            sx={{
              marginTop: 3,
              marginBottom: 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">
              {polyglot.t('freshwatch.invitation.rejected_message') ||
                'This invitation has been rejected.'}
            </Typography>
            <Typography variant="body2">
              {polyglot.t('freshwatch.invitation.please_contact_admin') ||
                'Please contact the company administrator for a new invitation.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={onBackToLogin}
            component={LinkRouter}
            to="/login"
          >
            {polyglot.t('freshwatch.invitation.back_to_login') ||
              'Go back to Login'}
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  // Handle EXPIRED invitation
  if (invitationId && !isInviationValid(invitation)) {
    return (
      <AuthLayout imageRight={SC_background}>
        <Box
          sx={{
            margin: 2,
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 80, color: 'warning.main', marginBottom: 2 }}
          />
          <Typography variant="h1" gutterBottom>
            {polyglot.t('freshwatch.invitation.expired_title') ||
              'Invitation Expired'}
          </Typography>
          <Alert
            severity="error"
            sx={{
              marginTop: 3,
              marginBottom: 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">
              {polyglot.t('freshwatch.invitation.expired_message') ||
                'This invitation has expired. Please contact the company administrator for a new invitation.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={onBackToLogin}
            component={LinkRouter}
            to="/login"
          >
            {polyglot.t('freshwatch.invitation.back_to_login') ||
              'Go back to Login'}
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  // Handle valid invitation (normal flow)
  if (!invitation) {
    return null;
  }

  return (
    <AuthLayout imageRight={SC_background}>
      <Box
        sx={{
          margin: 2,
          overflow: 'hidden',
        }}
      >
        <Typography variant="h1" gutterBottom>
          {polyglot.t('freshwatch.invitation.request_title') ||
            'Invitation Request'}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            padding: 3,
            marginTop: 3,
            marginBottom: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                {polyglot.t('freshwatch.invitation.welcome_message') ||
                  'You have been invited!'}
              </Typography>
              <Typography variant="body1">
                {polyglot.t('freshwatch.invitation.join_company_message') ||
                  'You have been invited to join to'}{' '}
                <strong>{invitation.companyName}</strong>
              </Typography>
            </Box>

            {invitation.personalMessage && (
              <Box
                sx={{
                  padding: 2,
                  marginTop: 2,
                  backgroundColor: 'background.paper',
                  'padding-inline': '1.5rem',
                  position: 'relative',
                }}
              >
                <Typography
                  variant="body2"
                  component={'blockquote'}
                  sx={{
                    marginX: '1.2rem',
                    ':before': {
                      content: '"“"',
                      fontFamily: 'serif',
                      position: 'absolute',
                      fontSize: '4rem',
                      color: (theme) => theme.palette.primary.main,
                      top: '-1rem',
                      left: '1rem',
                    },
                    ':after': {
                      content: '"”"',
                      fontFamily: 'serif',
                      position: 'absolute',
                      fontSize: '4rem',
                      color: (theme) => theme.palette.primary.main,
                      top: '-1rem',
                      right: '1rem',
                    },
                  }}
                >
                  {invitation.personalMessage}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {polyglot.t('freshwatch.invitation.detail.email') || 'Email:'}
              </Typography>
              <Typography variant="body1">{invitation.email}</Typography>
            </Box>

            {invitation.companyGroupName && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {polyglot.t('freshwatch.invitation.company_group_label') ||
                    'Company Group:'}
                </Typography>
                <Typography variant="body1">
                  {invitation.companyGroupName}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        <Stack spacing={2} direction="row" sx={{ marginTop: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onReject}
            disabled={isLoading}
          >
            {polyglot.t('button.back')}
          </Button>

          <LoadingButton
            color="primary"
            variant="contained"
            fullWidth
            onClick={onAccept}
            loading={isLoading}
            disabled={isLoading}
          >
            {polyglot.t('freshwatch.invitation.accept_button') ||
              'Accept Invitation'}
          </LoadingButton>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default InvitationRequestPage;
